<template>
  <div class="publish-page">
    <NavBar />
    
    <div class="page-container">
      <!-- 页面标题 -->
      <div class="page-header">
        <span class="back-link" @click="$router.push('/')">← 返回首页</span>
        <h1 class="page-title">发布信息</h1>
      </div>
      
      <!-- 发布表单 -->
      <div class="form-section">
        <div class="form-group">
          <label class="form-label">信息标题 <span class="required">*</span></label>
          <input 
            type="text" 
            class="form-input" 
            v-model="form.title" 
            placeholder="请输入信息标题"
            maxlength="100"
          />
        </div>
        
        <div class="form-row">
          <div class="form-group">
            <label class="form-label">姓名 <span class="required">*</span></label>
            <input 
              type="text" 
              class="form-input" 
              v-model="form.name" 
              placeholder="您的姓名"
            />
          </div>
          <div class="form-group">
            <label class="form-label">电话 <span class="required">*</span></label>
            <input 
              type="tel" 
              class="form-input" 
              v-model="form.phone" 
              placeholder="联系电话"
              maxlength="11"
            />
          </div>
        </div>
        
        <div class="form-group">
          <label class="form-label">邮箱</label>
          <input 
            type="email" 
            class="form-input" 
            v-model="form.email" 
            placeholder="邮箱地址（选填）"
          />
        </div>
        
        <div class="form-row">
          <div class="form-group">
            <label class="form-label">省份</label>
            <input 
              type="text" 
              class="form-input" 
              v-model="form.province" 
              placeholder="如：北京市"
            />
          </div>
          <div class="form-group">
            <label class="form-label">地址</label>
            <input 
              type="text" 
              class="form-input" 
              v-model="form.address" 
              placeholder="详细地址"
            />
          </div>
        </div>
        
        <div class="form-group">
          <label class="form-label">详细内容</label>
          <textarea 
            class="form-textarea" 
            v-model="form.content" 
            placeholder="请输入详细内容..."
            rows="5"
            maxlength="2000"
          ></textarea>
        </div>
        
        <div class="form-group">
          <label class="form-label">上传图片</label>
          <div class="upload-area">
            <a-upload
              :custom-request="handleUpload"
              :file-list="fileList"
              list-type="picture-card"
              :limit="6"
              @remove="handleRemove"
              accept="image/*"
            >
              <template #upload-button>
                <div class="upload-btn">
                  <span class="upload-icon">+</span>
                  <span class="upload-text">上传</span>
                </div>
              </template>
            </a-upload>
          </div>
          <p class="form-hint">最多6张，支持 jpg/png</p>
        </div>
        
        <div class="form-actions">
          <button 
            type="button" 
            class="btn-submit" 
            @click="handleSubmit"
            :disabled="submitting"
          >
            {{ submitting ? '发布中...' : '发布' }}
          </button>
          <button type="button" class="btn-reset" @click="handleReset">重置</button>
        </div>
        
        <p class="form-tip">发布成功后，可在 <a @click="$router.push('/profile')">个人中心</a> 查看管理</p>
      </div>
    </div>
    
    <Footer />
  </div>
</template>

<script>
import NavBar from '@/components/NavBar.vue'
import Footer from '@/components/Footer.vue'
import { publishInfo, uploadFile } from '@/api/index.js'

export default {
  name: 'PublishInfo',
  components: {
    NavBar,
    Footer
  },
  data() {
    return {
      form: {
        title: '',
        name: '',
        phone: '',
        email: '',
        province: '',
        address: '',
        content: '',
        images: ''
      },
      fileList: [],
      uploadedImages: [], // 存储相对路径
      uploadedFullUrls: [], // 存储完整URL用于预览
      submitting: false
    }
  },
  methods: {
    async handleUpload(option) {
      const { onError, onSuccess, fileItem } = option
      
      try {
        const formData = new FormData()
        formData.append('file', fileItem.file)
        
        const result = await uploadFile(formData)
        
        if (result.code === 1 && result.data) {
          // 存储相对路径用于提交
          this.uploadedImages.push(result.data.url)
          // 存储完整URL用于预览
          this.uploadedFullUrls.push(result.data.fullurl)
          onSuccess(result)
          this.$message.success('图片上传成功')
        } else {
          onError(result.msg || '上传失败')
          this.$message.error(result.msg || '上传失败')
        }
      } catch (error) {
        console.error('上传失败:', error)
        onError(error)
        this.$message.error('上传失败，请稍后重试')
      }
    },
    handleRemove(fileItem) {
      const index = this.fileList.findIndex(f => f.uid === fileItem.uid)
      if (index > -1) {
        this.uploadedImages.splice(index, 1)
        this.uploadedFullUrls.splice(index, 1)
      }
    },
    async handleSubmit() {
      // 验证必填项
      if (!this.form.title) {
        this.$message.warning('请输入信息标题')
        return
      }
      if (!this.form.name) {
        this.$message.warning('请输入发布人姓名')
        return
      }
      if (!this.form.phone) {
        this.$message.warning('请输入联系电话')
        return
      }
      
      // 验证手机号
      const phoneReg = /^1[3-9]\d{9}$/
      if (!phoneReg.test(this.form.phone)) {
        this.$message.warning('请输入正确的手机号码')
        return
      }
      
      // 验证邮箱（如果填写了）
      if (this.form.email) {
        const emailReg = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailReg.test(this.form.email)) {
          this.$message.warning('请输入正确的邮箱格式')
          return
        }
      }
      
      this.submitting = true
      
      try {
        // 组装图片路径
        const submitData = {
          ...this.form,
          images: this.uploadedImages.join(',')
        }
        
        const result = await publishInfo(submitData)
        
        if (result.code === 1) {
          this.$message.success('发布成功！您可以在个人中心查看')
          this.handleReset()
        } else {
          this.$message.error(result.msg || '发布失败')
        }
      } catch (error) {
        console.error('发布失败:', error)
        this.$message.error('发布失败，请稍后重试')
      } finally {
        this.submitting = false
      }
    },
    handleReset() {
      this.form = {
        title: '',
        name: '',
        phone: '',
        email: '',
        province: '',
        address: '',
        content: '',
        images: ''
      }
      this.fileList = []
      this.uploadedImages = []
      this.uploadedFullUrls = []
    }
  }
}
</script>

<style scoped>
.publish-page {
  min-height: 100vh;
  background: #fff;
}

.page-container {
  max-width: 600px;
  margin: 0 auto;
  padding: 40px 24px 80px;
}

.page-header {
  margin-bottom: 40px;
}

.back-link {
  font-size: 14px;
  color: #666;
  cursor: pointer;
  display: inline-block;
  margin-bottom: 20px;
  transition: color 0.2s;
}

.back-link:hover {
  color: #111;
}

.page-title {
  font-size: 32px;
  font-weight: 800;
  color: #111;
  margin: 0;
  padding-bottom: 16px;
  border-bottom: 2px solid #111;
}

.form-section {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.form-label {
  font-size: 14px;
  font-weight: 500;
  color: #111;
}

.form-label .required {
  color: #e00;
}

.form-input,
.form-textarea {
  width: 100%;
  padding: 12px 14px;
  font-size: 15px;
  border: 1px solid #ddd;
  outline: none;
  transition: border-color 0.2s;
  font-family: inherit;
  box-sizing: border-box;
}

.form-input:focus,
.form-textarea:focus {
  border-color: #111;
}

.form-textarea {
  resize: vertical;
  min-height: 120px;
}

.form-hint {
  font-size: 13px;
  color: #999;
  margin: 4px 0 0 0;
}

.upload-area {
  margin-top: 4px;
}

.upload-area :deep(.arco-upload-list-picture-card .arco-upload-list-picture-item) {
  border-radius: 0;
}

.upload-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #999;
}

.upload-icon {
  font-size: 24px;
  line-height: 1;
}

.upload-text {
  font-size: 12px;
  margin-top: 4px;
}

.form-actions {
  display: flex;
  gap: 12px;
  margin-top: 16px;
}

.btn-submit {
  flex: 1;
  padding: 14px;
  font-size: 15px;
  font-weight: 600;
  color: #fff;
  background: #111;
  border: none;
  cursor: pointer;
  transition: opacity 0.2s;
}

.btn-submit:hover:not(:disabled) {
  opacity: 0.8;
}

.btn-submit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-reset {
  padding: 14px 24px;
  font-size: 15px;
  font-weight: 500;
  color: #666;
  background: #fff;
  border: 1px solid #ddd;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-reset:hover {
  border-color: #111;
  color: #111;
}

.form-tip {
  font-size: 14px;
  color: #999;
  text-align: center;
  margin: 24px 0 0 0;
}

.form-tip a {
  color: #111;
  font-weight: 500;
  cursor: pointer;
  text-decoration: underline;
}

@media (max-width: 768px) {
  .page-container {
    padding: 24px 16px 60px;
  }
  
  .page-title {
    font-size: 26px;
  }
  
  .form-row {
    grid-template-columns: 1fr;
  }
  
  .form-actions {
    flex-direction: column;
  }
  
  .btn-reset {
    order: 1;
  }
  
  .btn-submit {
    order: 0;
  }
}
</style>
